import { FileDownloadOutlined, FileOpenOutlined } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemProps,
  listItemSecondaryActionClasses,
  Stack,
  stackClasses,
  styled,
  Tooltip,
} from '@mui/material';
import { Permission } from '@procureai/permissions';
import { Restricted } from '@procureai/permissions-ui';
import { useFileStorage } from '@procureai/uix';
import { useTranslation } from 'react-i18next';

import { DemandDocument } from '../../DemandDocument.types';
import { useDemandDocument as useDemandDocumentListItem } from '../../hooks/useDemandDocument/useDemandDocument';
import { NegotiationCaseLinkChip } from '../NegotiationCaseLinkChip/NegotiationCaseLinkChip';
import { I18nNamespace, FeatureFlag } from '~/common/types';

import { DocumentListItemText } from './DocumentListItemText';

const ListItemStyled = styled((props: ListItemProps) => <ListItem {...props} />)(({ theme }) => ({
  [`& > .${listItemSecondaryActionClasses.root} > .${stackClasses.root}`]: {
    [`& .downloadButton`]: {
      opacity: 0,
      transition: theme.transitions.create(['opacity']),
    },
    [`& .analyseButton`]: {
      display: 'none',
    },
  },
  [`&:hover > .${listItemSecondaryActionClasses.root} > .${stackClasses.root}`]: {
    [`& .downloadButton`]: {
      opacity: 1,
    },
    [`& .analyseButton`]: {
      display: 'block',
    },
  },
  [`& > .${listItemSecondaryActionClasses.root} > .${stackClasses.root} > .downloadButton:focus`]: {
    opacity: 1,
  },
}));

export interface DocumentListItemProps {
  file: DemandDocument;
  analyseDocumentAction?: React.ReactNode;
}

export const DocumentListItem = ({ file, analyseDocumentAction }: DocumentListItemProps) => {
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'DocumentListItem' });

  const item = useDemandDocumentListItem(file);
  const label = item.label;
  const { download } = useFileStorage();

  return item.label ? (
    <ListItemStyled
      disablePadding
      key={file.id}
      secondaryAction={
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Restricted needs={{ permission: Permission.DemandAnalyseDocument }}>
            {item.isAnalysisEligible && <Box className="analyseButton">{analyseDocumentAction}</Box>}
          </Restricted>
          {item.isSharedWithSupplier && (
            <Restricted needs={{ feature: FeatureFlag.FILE_SHARING }}>
              <Chip label={t('label.sharedWithSupplier')} color="warning" />
            </Restricted>
          )}
          {item.negotiationCase && (
            <Restricted needs={{ permission: Permission.CaseList }}>
              {item.negotiationCase && <NegotiationCaseLinkChip {...item.negotiationCase} />}
            </Restricted>
          )}
          {label && (
            <Tooltip title={t('tooltip.downloadFile')}>
              <IconButton className="downloadButton" onClick={() => download({ uploadId: item.id, fileName: label })}>
                <FileDownloadOutlined />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      }
    >
      <ListItemButton>
        <ListItemIcon>
          <FileOpenOutlined />
        </ListItemIcon>
        <DocumentListItemText primary={item.label} />
      </ListItemButton>
    </ListItemStyled>
  ) : null;
};
